import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { AdvertiseLayout } from '@/components/advertise/AdvertiseLayout';
import { AdvertiseSideSummary } from '@/components/advertise/AdvertiseSideSummary';
import { AdvertiseFooterNav } from '@/components/advertise/AdvertiseFooterNav';
import { useAdvertise } from '@/store/useAdvertise';
import { useStepGuard, agentsStepValid } from '@/utils/advertiseGuards';
import { AGENTS_STEPS } from '@/config/advertiseSteps';
import { submitLead } from '@/services/advertiseApi';
import { toast } from 'sonner';

const PLAN_LABEL = { membership: 'Agent Membership', boost: 'Listing Boost', both: 'Not sure — talk to sales' };

const Row = ({ label, value, editTo }) => (
  <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5 last:border-0">
    <dt className="text-sm text-slate-500">{label}</dt>
    <dd className="flex items-center gap-2 text-right text-sm font-medium text-slate-900">
      {value || '—'}
      {editTo && <Link to={editTo} className="text-blue-600"><Pencil size={13} /></Link>}
    </dd>
  </div>
);

export default function AgentsReview() {
  const navigate = useNavigate();
  const agents = useAdvertise((s) => s.agents);
  const setField = useAdvertise((s) => s.setField);
  const reset = useAdvertise((s) => s.reset);
  useStepGuard(agentsStepValid.review(agents), '/advertise/agents');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await submitLead('agents', {
        plan: agents.plan, firstName: agents.firstName, lastName: agents.lastName, email: agents.email,
        company: agents.company, phone: agents.phone, zip: agents.zip, mlsId: agents.mlsId,
        activeListings: agents.activeListings, markets: agents.markets, timeline: agents.timeline,
        competitors: agents.competitors, slotIso: agents.slotIso,
      });
      navigate('/advertise/agents/thank-you');
      reset('agents');
    } catch (e) {
      toast.error(e?.detail || e?.message || 'Something went wrong submitting your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdvertiseLayout
      trackLabel="Agent advertising"
      steps={AGENTS_STEPS}
      currentStepId="review"
      title="Review your request"
      subtitle="Double-check the details below before we reach out."
      sidebar={<AdvertiseSideSummary items={[{ label: 'Plan', value: PLAN_LABEL[agents.plan] }]} />}
      footer={
        <AdvertiseFooterNav
          onBack={() => navigate('/advertise/agents/schedule')}
          onNext={handleSubmit}
          nextDisabled={!agents.consent}
          loading={submitting}
          nextLabel="Submit request"
        />
      }
    >
      <dl>
        <Row label="Plan" value={PLAN_LABEL[agents.plan]} editTo="/advertise/agents/plan" />
        <Row label="Name" value={[agents.firstName, agents.lastName].filter(Boolean).join(' ')} editTo="/advertise/agents/profile" />
        <Row label="Email" value={agents.email} editTo="/advertise/agents/profile" />
        <Row label="Company" value={agents.company} editTo="/advertise/agents/profile" />
        <Row label="Phone" value={agents.phone} editTo="/advertise/agents/profile" />
        <Row label="Office ZIP" value={agents.zip} editTo="/advertise/agents/profile" />
        <Row label="Active listings" value={agents.activeListings} editTo="/advertise/agents/goals" />
        <Row label="Timeline" value={agents.timeline} editTo="/advertise/agents/goals" />
        <Row label="Markets" value={agents.markets.join(', ')} editTo="/advertise/agents/goals" />
        <Row label="Demo time" value={agents.slotLabel || 'Email follow-up'} editTo="/advertise/agents/schedule" />
      </dl>

      <label className="mt-6 flex cursor-pointer items-start gap-2.5">
        <Checkbox checked={agents.consent} onCheckedChange={(v) => setField('agents', { consent: !!v })} className="mt-0.5" data-testid="agents-review-consent" />
        <span className="text-sm text-slate-600">
          I agree to be contacted about casahom advertising products and accept the <Link to="/terms" className="text-blue-600 underline">Terms</Link> and <Link to="/privacy" className="text-blue-600 underline">Privacy Policy</Link>.
        </span>
      </label>
    </AdvertiseLayout>
  );
}
